/* Dashboard Builder — custom chart configuration form.
 *
 * Lets the user compose a query: dataset is the survey/risk fact; pick a
 * chart type, X dimension, Y measure, optional series (grouped/stacked/multi
 * line), and mix-and-match survey templates. Used in the Add-tile wizard and
 * to edit an existing custom tile. */
import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Label, Select } from '@/components/ui/input';
import { CustomQuery } from './types';

const DIMENSIONS = [
  { key: 'band', label: 'Risk band' }, { key: 'abm_type', label: 'ABM type' },
  { key: 'town', label: 'Town / City' }, { key: 'province', label: 'Province' },
  { key: 'template', label: 'Survey template' }, { key: 'broker', label: 'Broker' },
  { key: 'client', label: 'Client' }, { key: 'month', label: 'Submission month' },
  { key: 'score_band', label: 'Score band' },
];
const MEASURES = [
  { key: 'count', label: 'Count of surveys' }, { key: 'sum_cash', label: 'Total cash exposure' },
  { key: 'avg_cash', label: 'Avg cash exposure' }, { key: 'avg_score', label: 'Avg risk score' },
  { key: 'high_critical', label: 'High + Critical count' },
];
const CHARTS = [
  { key: 'bar', label: 'Bar' }, { key: 'stacked', label: 'Stacked / grouped bar' },
  { key: 'line', label: 'Line' }, { key: 'area', label: 'Area' },
  { key: 'pie', label: 'Pie' }, { key: 'donut', label: 'Donut' },
  { key: 'scatter', label: 'Scatter (X vs Y per site)' },
  { key: 'table', label: 'Table' }, { key: 'number', label: 'Single number (KPI)' },
];
const ROW_MEASURES = [{ key: 'cash', label: 'Cash exposure' }, { key: 'score', label: 'Risk score' }];

export const defaultQuery = (): CustomQuery => ({
  chart: 'bar', dimension: 'band', measure: 'count', series: '', limit: 20, sort: 'value_desc', template_ids: [],
});

interface Tpl { id: string; name: string }

export const CustomChartConfig: React.FC<{ value: CustomQuery; onChange: (q: CustomQuery) => void }>
  = ({ value, onChange }) => {
  const [templates, setTemplates] = useState<Tpl[]>([]);
  useEffect(() => {
    void api.get<Tpl[]>('/templates')
      .then((r) => setTemplates((r.data || []).map((t: any) => ({ id: t.id, name: t.name }))
        .sort((a: Tpl, b: Tpl) => a.name.localeCompare(b.name))))
      .catch(() => setTemplates([]));
  }, []);
  const set = (patch: Partial<CustomQuery>) => onChange({ ...value, ...patch });
  const chart = value.chart;
  const isNumber = chart === 'number';
  const isScatter = chart === 'scatter';
  const isCategory = !isNumber && !isScatter;
  const allowSeries = ['bar', 'stacked', 'line', 'area', 'table'].includes(chart);
  const selTpls = value.template_ids || [];
  const toggleTpl = (id: string) => set({
    template_ids: selTpls.includes(id) ? selTpls.filter((x) => x !== id) : [...selTpls, id],
  });

  return (
    <div className="space-y-3" data-testid="custom-chart-config">
      <div className="space-y-1.5">
        <Label>Chart type</Label>
        <Select value={chart} onChange={(e) => set({ chart: e.target.value as CustomQuery['chart'] })} data-testid="custom-chart-type">
          {CHARTS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </Select>
      </div>

      {isScatter && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>X axis</Label>
            <Select value={value.x_measure || 'cash'} onChange={(e) => set({ x_measure: e.target.value })} data-testid="custom-x-measure">
              {ROW_MEASURES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Y axis</Label>
            <Select value={value.y_measure || 'score'} onChange={(e) => set({ y_measure: e.target.value })} data-testid="custom-y-measure">
              {ROW_MEASURES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </Select>
          </div>
        </div>
      )}

      {isCategory && (
        <div className="space-y-1.5"><Label>X axis (group by)</Label>
          <Select value={value.dimension || 'band'} onChange={(e) => set({ dimension: e.target.value })} data-testid="custom-dimension">
            {DIMENSIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </Select>
        </div>
      )}

      {!isScatter && (
        <div className="space-y-1.5"><Label>Y axis (measure)</Label>
          <Select value={value.measure} onChange={(e) => set({ measure: e.target.value })} data-testid="custom-measure">
            {MEASURES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </Select>
        </div>
      )}

      {isCategory && allowSeries && (
        <div className="space-y-1.5"><Label>Split by (series — optional)</Label>
          <Select value={value.series || ''} onChange={(e) => set({ series: e.target.value })} data-testid="custom-series">
            <option value="">None</option>
            {DIMENSIONS.filter((d) => d.key !== value.dimension).map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </Select>
        </div>
      )}

      {isCategory && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Show top</Label>
            <Select value={String(value.limit || 20)} onChange={(e) => set({ limit: Number(e.target.value) })} data-testid="custom-limit">
              {[5, 10, 15, 20, 30, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Sort</Label>
            <Select value={value.sort || 'value_desc'} onChange={(e) => set({ sort: e.target.value as CustomQuery['sort'] })} data-testid="custom-sort">
              <option value="value_desc">Highest first</option>
              <option value="label_asc">By name (A–Z)</option>
            </Select>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <Label>Survey templates (mix &amp; match — none = all)</Label>
        <div className="max-h-28 overflow-auto rounded-md border border-border/60 p-2 space-y-1" data-testid="custom-templates">
          {templates.length === 0 && <div className="text-[11px] text-raptor-navy/40">No templates found.</div>}
          {templates.map((t) => (
            <label key={t.id} className="flex items-center gap-2 text-xs text-raptor-navy/80 cursor-pointer">
              <input type="checkbox" checked={selTpls.includes(t.id)} onChange={() => toggleTpl(t.id)} data-testid={`custom-tpl-${t.id}`} />
              {t.name}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};
